import { IconButton, InputAdornment, TextField } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import useDebounce from "hooks/useDebounce";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import DualListSelector, { DualListItem } from ".";
import { makeStyles } from "helpers/StyleHelper";

export type DualListSelectorSearchableProps = {
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  options: DualListItem[];
  selectedOptions: DualListItem[];
  isFetching?: boolean;
  readonly?: boolean;
  index: number;
  onChange: (selected: DualListItem[]) => void;
};

const useStyles = makeStyles((theme: Theme) => ({
  search: {
    "& input": {
      textTransform: "uppercase",
    },
    "& input::placeholder": {
      textTransform: "none",
    },
  },
}));

const DualListSelectorSearchable = ({
  setSearchTerm,
  options,
  selectedOptions,
  readonly,
  isFetching,
  index,
  onChange,
}: DualListSelectorSearchableProps) => {
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();
  const [searchText, setSearchText] = useState<string>("");
  const trimmedSearchTerm = searchText.trim();
  const debouncedSearchText = useDebounce(trimmedSearchTerm, 500);

  useEffect(() => {
    setSearchTerm(debouncedSearchText.toUpperCase());
  }, [debouncedSearchText, setSearchTerm]);

  const onChangeOptionsSelected = (selected: DualListItem[]) => {
    onChange(selected);
  };

  return (
    <>
      <TextField
        fullWidth
        variant="outlined"
        sx={styles.search}
        value={searchText ?? ""}
        placeholder={t({ id: "searchPlaceholder" })}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => setSearchText("")}>
                <ClearIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
        onChange={(e) => setSearchText(e.target.value)}
      />

      <DualListSelector
        options={options}
        selectedOptions={selectedOptions}
        onChange={onChangeOptionsSelected}
        isFetching={isFetching}
        readonly={readonly}
        index={index}
      />
    </>
  );
};

export default DualListSelectorSearchable;
